import { useEffect, useRef, useState } from 'react'

/** How often to poll for a new version (in milliseconds). Default: 2 minutes. */
const POLL_INTERVAL_MS = 2 * 60 * 1000

/**
 * Hook that polls `/build-meta.json` to detect when a new version of the app
 * has been deployed. Returns `true` when the server's build ID differs from
 * the build ID that was compiled into this bundle.
 *
 * The poll uses `cache: 'no-store'` to bypass the browser cache and always hit
 * the server. Polling is disabled in development (localhost).
 */
function useVersionCheck(): boolean {
  const [updateAvailable, setUpdateAvailable] = useState(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    // Skip polling in development
    const isDev = location.hostname === 'localhost' || location.hostname === '127.0.0.1'
    if (isDev) return

    const currentBuildId: string = __BUILD_ID__

    const checkVersion = async () => {
      try {
        const response = await fetch('/build-meta.json', {
          cache: 'no-store',
          headers: { Pragma: 'no-cache' }
        })

        if (!response.ok) return

        const data = await response.json()

        if (data.buildId && data.buildId !== currentBuildId) {
          setUpdateAvailable(true)
          // Stop polling once we know an update is available
          if (intervalRef.current) {
            clearInterval(intervalRef.current)
            intervalRef.current = null
          }
        }
      } catch {
        // Silently ignore fetch errors (e.g., user is offline).
        // We'll just try again on the next interval.
      }
    }

    // Run the first check after a short delay (don't block initial render)
    const initialTimeout = setTimeout(checkVersion, 10_000)

    // Then poll on a regular interval
    intervalRef.current = setInterval(checkVersion, POLL_INTERVAL_MS)

    return () => {
      clearTimeout(initialTimeout)
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [])

  return updateAvailable
}

export { useVersionCheck }
